// A job's summary read into the parts the office reads (job summary, step 1: no AI). A summary that came
// over with an imported job is the old system's HTML, written over its job type's template, so the one
// field holds several things: a call-recording link at the top, the template's own words left as they
// were, a sold job's form ("Sold by: …", "Work order on file: …", "Scheduling: …", "Scope: …",
// "Other: …"), and lines someone set in bold or **between stars** for dispatch. This reads them apart:
//   scope          the summary's own words: the recording link, the template's unchanged words, the
//                  form's empty labels and the "Possible recall:" heading taken out; the form's Scope
//                  is its scope; line breaks kept
//   form           the form's other answers
//   dispatchLines  the bold and starred lines, the stars taken off
//   original       the whole summary as text, nothing taken out: what was written is never lost
// Pure, and never throws: text it can't read comes back whole as its scope. A summary with no markup and
// nothing to take out comes back exactly as it was. The text rules are rich-text.ts's.
import { hasMarkup, richTextPlain } from './rich-text.ts';

export interface JobSummaryForm {
  soldBy: string | null;
  workOrderOnFile: string | null;
  scheduling: string | null;
  other: string | null;
}

export interface JobSummaryParts {
  /** The summary's own words, a line per line. */
  scope: string;
  form: JobSummaryForm;
  /** Lines marked for dispatch (bold, or between stars), the stars taken off. */
  dispatchLines: string[];
  /** The whole summary as text, nothing taken out. */
  original: string;
  /** Something was taken out of the scope or moved from it: the original says more than the scope. */
  changed: boolean;
}

// private-use characters marking where bold starts and ends, through richTextPlain
const ON = '';
const OFF = '';
const MARKS = /[]/g;

const ANY_TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9:-]*)((?:\s(?:[^<>"']|"[^"]*"|'[^']*')*)?)(\/?)>/g;
// the call-recording link a phone integration put at the top: a link around a picture saying "Start
// Recording" (and the picture alone, should the link be gone)
const RECORDING_LINK =
  /<a\b[^>]*>(?:(?!<\/a\s*>)[\s\S]){0,2000}?<img\b[^>]*\balt\s*=\s*(["']?)\s*start\s+recording\s*\1[^>]*>(?:(?!<\/a\s*>)[\s\S]){0,2000}?<\/a\s*>/gi;
const RECORDING_IMG = /<img\b[^>]*\balt\s*=\s*(["']?)\s*start\s+recording\s*\1[^>]*>/gi;
const BOLD_STYLE = /font-weight\s*:\s*(?:bold|bolder|[6-9]00)\b/i;

/** The HTML with the recording link gone and bold runs marked (ON … OFF) for richTextPlain to keep. */
function markBold(html: string): string {
  const spans: boolean[] = [];
  return html
    .replace(RECORDING_LINK, '')
    .replace(RECORDING_IMG, '')
    .replace(ANY_TAG, (tag: string, close: string, name: string, attrs: string, selfClose: string) => {
      const n = name.toLowerCase();
      if (n === 'b' || n === 'strong') return selfClose ? '' : close ? OFF : ON;
      if (n !== 'span' || selfClose) return tag;
      if (close) return spans.pop() ? `${OFF}${tag}` : tag;
      const bold = BOLD_STYLE.test(attrs);
      spans.push(bold);
      return bold ? `${ON}${tag}` : tag;
    });
}

const FORM_LABELS: [RegExp, keyof JobSummaryForm | 'scope'][] = [
  [/^sold\s*by$/i, 'soldBy'],
  [/^work\s*orders?\s*on\s*file$/i, 'workOrderOnFile'],
  [/^scheduling(?:\s*notes?)?$/i, 'scheduling'],
  [/^scope(?:\s*of\s*work)?$/i, 'scope'],
  [/^other$/i, 'other'],
];
const LABEL_LINE = /^([A-Za-z][A-Za-z ]{1,24}?)\s*[:：]\s*(.*)$/;
const RECALL_HEADING = /^possible\s+recall\s*[:：]?$/i;
// a line opening with two stars, or between stars ("**Call before dispatch**", "*Prefers mornings*");
// one star alone in front is a list's bullet
const STARRED = /^(\*+)\s*(.*?)\s*(\**)$/;
const starred = (text: string): string | null => {
  const m = STARRED.exec(text);
  if (!m || !WORD.test(m[2]!)) return null;
  return m[1]!.length >= 2 || m[3]!.length >= 1 ? m[2]! : null;
};
const WORD = /[\p{L}\p{N}]/u;

const squash = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();

/** The form label a line opens with ("Sold by: Ann" → soldBy, "Ann"), or null. */
function formLabel(line: string): { key: keyof JobSummaryForm | 'scope'; value: string } | null {
  const m = LABEL_LINE.exec(line);
  if (!m) return null;
  const label = m[1]!.trim();
  const hit = FORM_LABELS.find(([re]) => re.test(label));
  return hit ? { key: hit[1], value: m[2]!.trim() } : null;
}

interface Line {
  /** As written, for text with no markup; else as richTextPlain made it. */
  raw: string;
  /** Trimmed, the bold marks taken off. */
  text: string;
  /** Every word on it is bold. */
  bold: boolean;
}

/** The marked text's lines, each with whether all its words are bold (a bold run can span lines). */
function linesOf(marked: string): Line[] {
  let depth = 0;
  return marked.split('\n').map((l) => {
    let words = 0;
    let plain = 0;
    for (const ch of l) {
      if (ch === ON) depth++;
      else if (ch === OFF) depth = Math.max(0, depth - 1);
      else if (WORD.test(ch)) {
        words++;
        if (!depth) plain++;
      }
    }
    const raw = l.replace(MARKS, '');
    return { raw, text: raw.trim(), bold: words > 0 && plain === 0 };
  });
}

/** Whether a line is the template's own words, unchanged: one of its lines, or a sentence it starts with. */
function fromTemplate(text: string, lines: Set<string>, whole: string): boolean {
  const t = squash(text);
  if (!t || !WORD.test(t)) return false;
  if (lines.has(t) || t === whole) return true;
  // a sentence the template starts with, left as it was ("Time and material billing.")
  return t.length >= 12 && /[.!?]$/.test(t) && whole.startsWith(t);
}

const NO_FORM: JobSummaryForm = { soldBy: null, workOrderOnFile: null, scheduling: null, other: null };

/**
 * A job's summary (HTML or plain text) read into its parts, given its job type's template (the text the
 * summary was pre-filled with), when there is one.
 */
export function readJobSummary(
  summary: string | null | undefined,
  template?: string | null,
): JobSummaryParts {
  const given = summary ?? '';
  let original = given;
  try {
    original = richTextPlain(given);
    return read(given, original, template ?? null);
  } catch {
    // odd text never fails the page: it is its own scope
    return { scope: original, form: { ...NO_FORM }, dispatchLines: [], original, changed: false };
  }
}

function read(given: string, original: string, template: string | null): JobSummaryParts {
  const markup = hasMarkup(given);
  // marked bold (or the recording link gone) means it had tags: read as HTML, the marks kept
  const withMarks = markup ? markBold(given) : given;
  const lines = linesOf(
    withMarks === given ? (markup ? original : given) : richTextPlain(`<div>${withMarks}</div>`),
  );
  const tplText = template ? richTextPlain(template) : '';
  const tplLines = new Set(
    tplText
      .split('\n')
      .map(squash)
      .filter((l) => WORD.test(l)),
  );
  const tplWhole = squash(tplText);
  // a form's labels are read as one where the template has them, or where the summary has two or more
  const labelled = lines.map((l) => formLabel(l.text));
  const kinds = new Set(labelled.filter(Boolean).map((f) => f!.key));
  const isForm = (f: ReturnType<typeof formLabel>) =>
    !!f && (kinds.size >= 2 || [...tplLines].some((t) => formLabel(t)?.key === f.key));

  const form: JobSummaryForm = { ...NO_FORM };
  const kept: Line[] = [];
  const marked: { line: Line; starred: string | null }[] = [];
  let dropped = false;
  lines.forEach((line, i) => {
    const f = labelled[i];
    if (!line.text) return void kept.push(line);
    if (RECALL_HEADING.test(line.text)) return void (dropped = true);
    if (f && isForm(f)) {
      dropped = true;
      if (!f.value) return;
      // the form's Scope is the scope, never a dispatch line, however it was set
      if (f.key === 'scope') return void kept.push({ raw: f.value, text: f.value, bold: false });
      form[f.key] = form[f.key] ? `${form[f.key]}; ${f.value}` : f.value;
      return;
    }
    if (tplWhole && fromTemplate(line.text, tplLines, tplWhole)) return void (dropped = true);
    const star = starred(line.text);
    if (star !== null) return void marked.push({ line, starred: star });
    if (line.bold) {
      marked.push({ line, starred: null });
      kept.push(line);
      return;
    }
    kept.push(line);
  });
  // bold is for dispatch only where the rest isn't bold too: a summary all in bold is just its words
  const plainLeft = kept.some((l) => l.text && !l.bold);
  const dispatchLines = marked
    .filter((m) => m.starred !== null || plainLeft)
    .map((m) => m.starred ?? m.line.text);
  const scopeLines = kept.filter((l) => !(l.bold && plainLeft && l.text));
  const moved = dispatchLines.length > 0;
  if (!markup && !dropped && !moved) return { scope: given, form, dispatchLines, original, changed: false };
  const scope = scopeLines
    .map((l) => (markup ? l.text : l.raw))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^(?:[ \t]*\n)+/, '')
    .replace(/(?:\n[ \t]*)+$/, '');
  const finalScope = markup ? scope.trim() : scope;
  return { scope: finalScope, form, dispatchLines, original, changed: finalScope !== original.trim() };
}

/** A summary's first lines, for a hover card: at most `n` non-empty lines of its scope. */
export function scopeLead(scope: string, n = 2): string {
  return scope
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, n)
    .join('\n');
}

/**
 * The imported record's priority and its "Priority Level" field, in one: "High · level 2". Null with
 * neither.
 */
export function importedPriorityText(priority: string | null, level: string | null): string | null {
  const p = priority?.trim() || null;
  const l = level?.trim() || null;
  if (p && l) return `${p} · level ${l}`;
  if (l) return `Level ${l}`;
  return p;
}

/**
 * A custom field's value on an imported record (`customFields: [{ name, value }]`), by its name as
 * people read it: the case and the spaces around it don't matter ("Buzzer Number ", "buzzer number").
 * Null when it isn't there or says nothing.
 */
export function customFieldValue(fields: unknown, name: string): string | null {
  if (!Array.isArray(fields)) return null;
  const want = squash(name);
  for (const f of fields) {
    if (!f || typeof f !== 'object') continue;
    const r = f as { name?: unknown; value?: unknown };
    if (typeof r.name !== 'string' || squash(r.name) !== want) continue;
    const v =
      typeof r.value === 'string' ? r.value.trim() : typeof r.value === 'number' ? String(r.value) : '';
    if (v) return v;
  }
  return null;
}
